import { ShieldCheck, FileSearch, GitMerge, ArrowRight, FileOutput } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { CptSelectorDemo } from './CptSelectorDemo';

const pillars = [
  {
    icon: FileSearch,
    number: '02',
    title: 'Intra-Op Evidence',
    subtitle: 'Proof Mapping Logic',
    body: 'High-fidelity dictation capture is parsed through Proof Mapping—extracting CPT evidence directly from the clinical narrative without inventing facts.',
    outcome: 'Every billed code is linked to documented clinical evidence.',
  },
  {
    icon: GitMerge,
    number: '03',
    title: 'Post-Op Reconciliation',
    subtitle: 'The Hard Gate',
    body: 'A three-way reconciliation engine ensures a code only reaches billing if it is Authorized, Documented, and Surgeon-Confirmed. No exceptions.',
    outcome: 'Authorization = Documentation = Export. Always.',
  },
];

function PillarCard({ pillar }: { pillar: typeof pillars[number] }) {
  return (
    <Card className="bg-card border border-border rounded-xl transition-all hover:shadow-lg">
      <CardContent className="p-8 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-mono text-muted-foreground tracking-widest">
            {pillar.number}
          </span>
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 border border-primary/20">
            <pillar.icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
          </div>
        </div>
        <div>
          <h3 className="text-xl font-semibold text-foreground mb-1">
            {pillar.title}
          </h3>
          <p className="text-xs text-primary font-medium">
            {pillar.subtitle}
          </p>
        </div>
        <p className="text-muted-foreground leading-relaxed">
          {pillar.body}
        </p>
        <div className="flex items-start gap-2 pt-2 border-t border-border">
          <ArrowRight className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-xs text-primary font-medium leading-relaxed">
            {pillar.outcome}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

export function FourPillarsSection() {
  return (
    <section id="closed-loop-system" className="py-20 md:py-28 bg-secondary">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section header */}
        <div className="text-center mb-14 md:mb-16">
          <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">
            Platform Architecture
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-6">
            Three Pillars. One Standard.
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Every billable code passes through three independent enforcement
            layers before it reaches your billing system.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Pillar 1 */}
          <Card className="bg-card border border-border rounded-xl transition-all hover:shadow-lg">
            <CardContent className="p-8 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-muted-foreground tracking-widest">01</span>
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 border border-primary/20">
                  <ShieldCheck className="h-5 w-5 text-primary" strokeWidth={1.5} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-1">Pre-Op Authorization</h3>
                <p className="text-xs text-primary font-medium">Intelligent Subset Bundles</p>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                LINK auto-expands primary procedures into comprehensive authorization
                bundles—covering primary codes and plausible variations before the
                surgeon enters the OR.
              </p>
              <div className="pt-2">
                <CptSelectorDemo />
              </div>
              <div className="flex items-start gap-2 pt-2 border-t border-border">
                <ArrowRight className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-primary font-medium leading-relaxed">
                  Prevents un-authorized work from reaching the billing office.
                </p>
              </div>
            </CardContent>
          </Card>

          {pillars.map((pillar) => (
            <PillarCard key={pillar.number} pillar={pillar} />
          ))}
        </div>

        {/* Result Card */}
        <div className="mt-8 md:mt-10 max-w-5xl mx-auto">
          <Card className="bg-card border border-primary/20 rounded-xl">
            <CardContent className="p-8 flex flex-col md:flex-row items-start md:items-center gap-8">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 shrink-0">
                <FileOutput className="h-6 w-6 text-primary" strokeWidth={1.5} />
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-foreground">
                  The Result: Audit-Defensible Exports
                </h3>
                <p className="text-muted-foreground leading-relaxed max-w-3xl">
                  Once the Three Pillars are satisfied, LINK generates an Epic-ready export package
                  including the finalized CPT string and a PDF Audit Summary with evidence snippets
                  for permanent record.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}
